import { CommonModule } from '@angular/common';
import { Component, computed, inject, type OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import type { PublicCandidateApplicationDTO } from '@libs/dtos/merit/candidate.dto';
import { VIDEO_MAX_BYTES } from '@libs/dtos/merit/onboarding.dto';
import { CandidateApiService } from '@services/candidate/candidate-api.service';
import { MeritI18nService } from '@services/merit/merit-i18n.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { FileUploadComponent } from '@shared/components/file-upload/file-upload.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';

/**
 * Public completion page (Fase 3). Reached via the single-use `/complete/:token`
 * link emailed to a candidate after scoring (approved or threshold-rejected). Shows
 * their application read-only (never the score) and lets them upload a presentation
 * video.
 */
@Component({
	selector: 'app-candidate-complete',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		TranslocoModule,
		FileUploadComponent,
		ButtonComponent,
		SpinnerComponent,
	],
	templateUrl: './candidate-complete.component.html',
	styleUrl: './candidate-complete.component.scss',
})
export class CandidateCompleteComponent implements OnInit {
	private readonly route = inject(ActivatedRoute);
	private readonly api = inject(CandidateApiService);
	private readonly transloco = inject(TranslocoService);
	readonly meritI18n = inject(MeritI18nService);

	readonly loading = signal(true);
	readonly notFound = signal(false);
	readonly submitting = signal(false);
	readonly done = signal(false);
	readonly serverError = signal<string | null>(null);
	readonly application = signal<PublicCandidateApplicationDTO | null>(null);

	private token = '';

	readonly videoFiles = new FormControl<File[]>([], { nonNullable: true });

	/** Languages with localized name + level hint (mirrors the admin queue view). */
	readonly languageSkills = computed(() =>
		(this.application()?.languages ?? []).map((l) => ({
			language: this.meritI18n.language(l.language),
			level: l.level,
			hint: this.meritI18n.level(l.level),
		})),
	);

	/** Flat label/value rows for the read-only application (languages rendered apart). */
	readonly applicationRows = computed<{ labelKey: string; value: string }[]>(() => {
		const c = this.application();
		if (!c) return [];
		const dash = '—';
		const opt = (v?: string | null) => (v ? this.meritI18n.option(v) : dash);
		const pos = (v?: string, other?: string | null) =>
			v === 'Other' && other ? `${this.meritI18n.option(v)} (${other})` : opt(v);
		const f = 'publicForms.candidateApply.fields.';
		// A single GCC location: "Other" resolves to the free "City, Country" text.
		const loc = (v?: string | null, oc?: string | null, ct?: string | null) =>
			v === 'Other' ? [ct, oc].filter(Boolean).join(', ') || opt(v) : opt(v);
		// A list of GCC locations for the multi-select Location Targeting.
		const locList = (arr?: string[] | null, oc?: string | null, ct?: string | null) => {
			const list = arr ?? [];
			if (!list.length) return dash;
			return list.map((x) => loc(x, oc, ct)).join(' · ');
		};
		return [
			{ labelKey: `${f}fullName`, value: c.fullName || dash },
			{ labelKey: `${f}phone`, value: c.phone || dash },
			{ labelKey: 'common.labels.email', value: c.email || dash },
			// El código de país manda sobre el texto libre antiguo.
			{ labelKey: `${f}nationality`, value: this.meritI18n.nationality(c.countryCode, c.nationality) || dash },
			{ labelKey: `${f}availability`, value: opt(c.availability) },
			{ labelKey: `${f}yearsOfExperience`, value: opt(c.yearsOfExperience) },
			{ labelKey: `${f}lastPosition`, value: pos(c.lastPosition, c.lastPositionOther) },
			{ labelKey: `${f}targetPosition`, value: pos(c.targetPosition, c.targetPositionOther) },
			{ labelKey: `${f}drivingLicence`, value: opt(c.drivingLicence) },
			{ labelKey: `${f}salaryExpectation`, value: opt(c.salaryExpectation) },
			{ labelKey: `${f}visaStatus`, value: opt(c.visaStatus) },
			{ labelKey: `${f}luxuryLevel`, value: opt(c.luxuryLevel) },
			{ labelKey: `${f}managementExperience`, value: opt(c.managementExperience) },
			{
				labelKey: `${f}currentLocation`,
				value: loc(c.currentLocation, c.currentLocationOtherCountry, c.currentLocationOtherCity),
			},
			{ labelKey: `${f}gccExperience`, value: opt(c.gccExperience) },
			{
				labelKey: `${f}locationTargeting`,
				value: locList(c.locationTargeting, c.locationTargetingOtherCountry, c.locationTargetingOtherCity),
			},
			{ labelKey: `${f}linkedinUrl`, value: c.linkedinUrl || dash },
		];
	});

	ngOnInit(): void {
		this.token = this.route.snapshot.paramMap.get('token') ?? '';
		if (!this.token) {
			this.loading.set(false);
			this.notFound.set(true);
			return;
		}
		// This public candidate-facing page is always English (international talent),
		// regardless of the app's default locale. Switch the locale AND wait for the
		// `en` bundle to load before fetching, so imperatively-read labels (e.g. the
		// CEFR level names via meritI18n.level) resolve in English instead of falling
		// back to the still-loaded `es` while `en` is in flight.
		this.transloco.setActiveLang('en');
		this.transloco.load('en').subscribe({
			next: () => this.loadApplication(),
			error: () => this.loadApplication(),
		});
	}

	private loadApplication(): void {
		this.api.getByCompletionToken(this.token).subscribe({
			next: (app) => {
				this.application.set(app);
				this.loading.set(false);
			},
			error: () => {
				this.notFound.set(true);
				this.loading.set(false);
			},
		});
	}

	submit(): void {
		this.serverError.set(null);
		const files = this.videoFiles.value ?? [];
		const video = files[0];
		if (!video) {
			this.serverError.set(this.transloco.translate('publicForms.candidateComplete.messages.videoRequired'));
			return;
		}
		// El tope del vídeo, no el general: son el mismo número hoy (200 MB), pero
		// el general es el techo de la petición entera y el del vídeo es el que
		// aplica el servidor. Atarlos por casualidad es lo que hace que dentro de
		// dos meses uno cambie y el otro no.
		if (video.size > VIDEO_MAX_BYTES) {
			this.serverError.set(
				this.transloco.translate('publicForms.common.messages.fileTooLargeField', {
					field: this.transloco.translate('publicForms.fileFields.video'),
					name: video.name,
					max: Math.round(VIDEO_MAX_BYTES / (1024 * 1024)),
				}),
			);
			return;
		}

		this.submitting.set(true);
		this.api.submitCompletionVideo(this.token, video).subscribe({
			next: () => {
				this.submitting.set(false);
				this.done.set(true);
			},
			error: (err) => {
				this.submitting.set(false);
				// 404 → the token was already used/invalid; 413 → body too large at the proxy.
				const key =
					err?.status === 404
						? 'publicForms.candidateComplete.messages.invalidLink'
						: err?.status === 413
							? 'publicForms.common.messages.fileTooLarge'
							: 'publicForms.candidateComplete.messages.genericError';
				this.serverError.set(this.transloco.translate(key));
			},
		});
	}
}
